import { CommonModule } from '@angular/common';
import { AuditDirectoryCellComponent } from '../../shared/audit/audit-directory-cell.component';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { WarehousingMasterType, masterDataMap, ownPhysicalWarehouses, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import {
  WarehousingItem,
  WarehousingMasterData,
  WarehousingSearchFilters,
  WarehousingStockReportGroup,
  WarehousingStockReportResponse,
  WarehousingStockReportRow,
  WarehousingStockReportTotals
} from '../warehousing.model';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingSearchPanelComponent } from '../warehousing-search-panel.component';
import { itemSizeLabel } from '../warehousing-item-display';
import { WarehousingService } from '../warehousing.service';

type ReportFlatRow =
  | { type: 'group'; id: string; depth: number; group: WarehousingStockReportGroup }
  | { type: 'detail'; id: string; depth: number; detail: WarehousingStockReportRow };

@Component({
  selector: 'app-warehousing-stock',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, DatePickerModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputTextModule, MultiSelectModule, ProgressBarModule, SelectModule,
    TableModule, TagModule, WarehousingPageHeaderComponent, WarehousingSearchPanelComponent, AuditDirectoryCellComponent
  ],
  templateUrl: './warehousing-stock.component.html'
})
export class WarehousingStockComponent implements OnInit {
  private fb = inject(FormBuilder);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private messageService = inject(MessageService);

  isLoading = false;
  isExporting = false;
  isAuditExporting = false;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('On Hand Stock Report');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  report?: WarehousingStockReportResponse;
  flatRows: ReportFlatRow[] = [];
  expandedGroups = new Set<string>();
  private leafDetailsByGroup = new Map<string, WarehousingStockReportRow[]>();
  masterData = masterDataMap();
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  items: WarehousingItem[] = [];

  readonly defaultGroupBy = ['WAREHOUSE', 'CLIENT', 'CATEGORY', 'ITEM', 'STATUS'];
  readonly metricSelection = ['ON_HAND', 'AVAILABLE', 'RESERVED', 'UNIT_COUNT', 'SQM', 'CBM'];
  readonly groupByOptions = [
    { label: 'Region', value: 'REGION' },
    { label: 'Warehouse', value: 'WAREHOUSE' },
    { label: 'Zone', value: 'ZONE' },
    { label: 'Client', value: 'CLIENT' },
    { label: 'Account', value: 'ACCOUNT' },
    { label: 'Category', value: 'CATEGORY' },
    { label: 'Brand', value: 'BRAND' },
    { label: 'Sub Brand', value: 'SUB_BRAND' },
    { label: 'Item', value: 'ITEM' },
    { label: 'Item Condition', value: 'ITEM_CONDITION' },
    { label: 'Status', value: 'STATUS' },
    { label: 'Tracking Mode', value: 'TRACKING_MODE' },
    { label: 'Received Month', value: 'RECEIVED_MONTH' }
  ];
  readonly stockScopeOptions = [
    { label: 'On Hand Only', value: 'ON_HAND_ONLY' },
    { label: 'Available Only', value: 'AVAILABLE_ONLY' },
    { label: 'Reserved Only', value: 'RESERVED_ONLY' },
    { label: 'Refurbishment', value: 'REFURBISHMENT' },
    { label: 'All Stock States', value: 'ALL_STOCK_STATES' }
  ];
  readonly stockStatusOptions = [
    { label: 'Available', value: 'AVAILABLE' },
    { label: 'Reserved', value: 'RESERVED' },
    { label: 'Dispatched', value: 'DISPATCHED' },
    { label: 'Under Refurbishment', value: 'UNDER_REFURBISHMENT' },
    { label: 'Destroyed', value: 'DESTROYED' }
  ];
  readonly trackingModeOptions = [
    { label: 'Quantity', value: 'QUANTITY' },
    { label: 'Unit-level Tracking Required', value: 'UNIT' }
  ];

  searchForm = this.fb.group({
    keyword: [''],
    stockScope: ['ON_HAND_ONLY'],
    groupBy: [this.defaultGroupBy],
    crmClientId: [null as number | null],
    crmAccountId: [null as number | null],
    itemId: [null as number | null],
    categoryId: [null as number | null],
    brandId: [null as number | null],
    subBrandId: [null as number | null],
    regionId: [null as number | null],
    warehouseId: [null as number | null],
    zoneId: [null as number | null],
    conditionId: [null as number | null],
    status: [null as string | null],
    trackingMode: [null as string | null],
    receivedFrom: [null as Date | null],
    receivedTo: [null as Date | null]
  });

  ngOnInit(): void {
    this.loadInitialData();
  }

  loadInitialData(): void {
    this.isLoading = true;
    forkJoin({
      report: this.warehousingService.getStockReport(this.searchFilters()),
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      items: this.warehousingService.getItems(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION'),
      categories: this.warehousingService.getMasterData('CATEGORY'),
      brands: this.warehousingService.getMasterData('BRAND'),
      subBrands: this.warehousingService.getMasterData('SUB_BRAND')
    }).subscribe({
      next: response => {
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.items = response.items.data || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = ownPhysicalWarehouses(response.warehouses.data || []);
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.masterData.CATEGORY = response.categories.data || [];
        this.masterData.BRAND = response.brands.data || [];
        this.masterData.SUB_BRAND = response.subBrands.data || [];
        this.applyReport(response.report.data);
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  loadReport(): void {
    this.isLoading = true;
    this.warehousingService.getStockReport(this.searchFilters()).subscribe({
      next: response => {
        this.applyReport(response.data);
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  applySearch(): void {
    this.loadReport();
  }

  clearSearch(): void {
    this.searchForm.reset({
      keyword: '',
      stockScope: 'ON_HAND_ONLY',
      groupBy: this.defaultGroupBy,
      crmClientId: null,
      crmAccountId: null,
      itemId: null,
      categoryId: null,
      brandId: null,
      subBrandId: null,
      regionId: null,
      warehouseId: null,
      zoneId: null,
      conditionId: null,
      status: null,
      trackingMode: null,
      receivedFrom: null,
      receivedTo: null
    });
    this.loadReport();
  }

  selectedGroupBy(): string[] {
    return this.searchForm.value.groupBy || [];
  }

  moveGroupBy(index: number, direction: -1 | 1): void {
    const values = [...this.selectedGroupBy()];
    const target = index + direction;
    if (target < 0 || target >= values.length) return;
    [values[index], values[target]] = [values[target], values[index]];
    this.searchForm.patchValue({ groupBy: values });
    this.loadReport();
  }

  removeGroupBy(value: string): void {
    const values = this.selectedGroupBy().filter(item => item !== value);
    this.searchForm.patchValue({ groupBy: values.length ? values : this.defaultGroupBy });
    this.loadReport();
  }

  toggleGroup(row: ReportFlatRow): void {
    if (row.type !== 'group') return;
    if (this.expandedGroups.has(row.id)) {
      this.expandedGroups.delete(row.id);
    } else {
      this.expandedGroups.add(row.id);
    }
    this.rebuildFlatRows();
  }

  expandAll(): void {
    this.collectGroupIds(this.report?.groupedRows || []).forEach(id => this.expandedGroups.add(id));
    this.rebuildFlatRows();
  }

  collapseAll(): void {
    this.expandedGroups.clear();
    this.rebuildFlatRows();
  }

  exportReport(format: 'xlsx' | 'csv'): void {
    this.isExporting = true;
    this.warehousingService.exportStockReport(this.searchFilters(), format).subscribe({
      next: blob => {
        this.downloadBlob(blob, `on-hand-stock-report.${format}`);
        this.isExporting = false;
      },
      error: () => this.isExporting = false
    });
  }

  exportWarehouseAudit(): void {
    const crmClientId = this.searchForm.getRawValue().crmClientId;
    if (!crmClientId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Select a CRM client',
        detail: 'Choose one client before exporting its branded warehouse stock audit.'
      });
      return;
    }
    this.isAuditExporting = true;
    this.warehousingService.exportWarehouseStockAudit(this.searchFilters()).subscribe({
      next: blob => {
        const client = this.clients.find(row => row.id === crmClientId);
        const clientToken = (client?.companyName || 'client').toLowerCase()
          .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'client';
        this.downloadBlob(blob, `warehouse-stock-audit-${clientToken}.pdf`);
        this.isAuditExporting = false;
      },
      error: error => {
        this.isAuditExporting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Warehouse audit unavailable',
          detail: error?.error?.message || 'No branded on-hand stock audit could be generated for the selected client.'
        });
      }
    });
  }

  canExportWarehouseAudit(): boolean {
    return !!this.searchForm.getRawValue().crmClientId;
  }

  openLedger(stockUnitId?: number): void {
    if (!stockUnitId) {
      return;
    }
    window.location.assign(`/warehousing/stock-ledger?stockUnitId=${stockUnitId}`);
  }

  itemOptions(): (WarehousingItem & { displayLabel: string })[] {
    return this.items.map(item => ({ ...item, displayLabel: this.itemOptionLabel(item) }));
  }

  totals(): WarehousingStockReportTotals {
    return this.report?.totals || {
      onHandQuantity: 0,
      availableQuantity: 0,
      reservedQuantity: 0,
      unitCount: 0,
      occupiedSqm: 0,
      occupiedCbm: 0
    };
  }

  dimensionLabel(value: string): string {
    return this.groupByOptions.find(option => option.value === value)?.label || value;
  }

  statusSeverity = statusSeverity;

  itemMeta(row?: WarehousingStockReportRow): string {
    if (!row) return '';
    return [
      row.itemNumber ? `Item # ${row.itemNumber}` : null,
      row.skuNumber ? `SKU ${row.skuNumber}` : null,
      row.customerItemNumber ? `Customer ${row.customerItemNumber}` : null,
      row.tenantUomName ? `UOM ${row.tenantUomName}` : null
    ].filter(Boolean).join(' | ');
  }

  itemSize(row?: WarehousingStockReportRow): string {
    return itemSizeLabel(row ? {
      lengthUnitName: row.lengthUnitName,
      lengthMeters: row.length,
      breadthMeters: row.breadth,
      heightMeters: row.height
    } : null);
  }

  itemDetails(row?: WarehousingStockReportRow): string {
    if (!row) return '';
    return [
      this.itemSize(row),
      row.weightKg !== undefined ? `Weight ${this.formatNumber(row.weightKg)} KG` : null,
      `SQM ${this.formatNumber(row.sqmPerUnit)}`,
      `CBM ${this.formatNumber(row.cbmPerUnit)}`,
      row.categoryPath,
      row.brandName,
      row.subBrandName,
      row.trackingMode
    ].filter(Boolean).join(' | ');
  }

  rowPadding(depth: number): string {
    return `${depth * 1.25}rem`;
  }

  isExpanded(row: ReportFlatRow): boolean {
    return row.type === 'group' && this.expandedGroups.has(row.id);
  }

  groupHasChildren(row: ReportFlatRow): boolean {
    return row.type === 'group' && (!!row.group.children?.length
      || !!row.group.detailRows?.length || this.leafDetailsByGroup.has(row.id));
  }

  private applyReport(report?: WarehousingStockReportResponse): void {
    this.report = report;
    this.indexLeafDetails(report);
    this.expandedGroups.clear();
    (report?.groupedRows || []).forEach((group, index) => this.expandedGroups.add(this.groupId(group, index.toString())));
    this.rebuildFlatRows();
  }

  private rebuildFlatRows(): void {
    this.flatRows = this.flattenGroups(this.report?.groupedRows || []);
  }

  private flattenGroups(groups: WarehousingStockReportGroup[], parentPath = ''): ReportFlatRow[] {
    const rows: ReportFlatRow[] = [];
    groups.forEach((group, index) => {
      const path = this.groupId(group, parentPath ? `${parentPath}.${index}` : index.toString());
      rows.push({ type: 'group', id: path, depth: group.depth || 0, group });
      if (!this.expandedGroups.has(path)) return;
      rows.push(...this.flattenGroups(group.children || [], path));
      const detailRows = this.leafDetailsByGroup.get(path) || group.detailRows || [];
      detailRows.forEach(detail => rows.push({
        type: 'detail',
        id: `${path}.unit.${detail.stockUnitId}`,
        depth: (group.depth || 0) + 1,
        detail
      }));
    });
    return rows;
  }

  /**
   * Compact API responses keep each row only once at report level. Reattach
   * those rows to their leaf group locally so the existing expand/collapse
   * experience remains unchanged without duplicating a multi-megabyte payload.
   */
  private indexLeafDetails(report?: WarehousingStockReportResponse): void {
    this.leafDetailsByGroup.clear();
    const rootGroups = report?.groupedRows || [];
    (report?.detailRows || []).forEach(detail => {
      let groups = rootGroups;
      let parentPath = '';
      while (groups.length) {
        const dimension = groups[0]?.dimension;
        const index = groups.findIndex(group => group.key === this.groupKey(detail, dimension));
        if (index < 0) return;
        const group = groups[index];
        const path = this.groupId(group, parentPath ? `${parentPath}.${index}` : index.toString());
        if (!group.children?.length) {
          const rows = this.leafDetailsByGroup.get(path) || [];
          rows.push(detail);
          this.leafDetailsByGroup.set(path, rows);
          return;
        }
        parentPath = path;
        groups = group.children;
      }
    });
  }

  private groupKey(row: WarehousingStockReportRow, dimension?: string): string {
    const id = (value?: number) => value === null || value === undefined ? 'UNSPECIFIED' : String(value);
    const text = (value?: string, fallback = 'Unspecified') => value?.trim() || fallback;
    switch (dimension) {
      case 'REGION': return id(row.regionId);
      case 'WAREHOUSE': return id(row.warehouseId);
      case 'ZONE': return id(row.zoneId);
      case 'CLIENT': return id(row.crmClientId);
      case 'ACCOUNT': return id(row.crmAccountId);
      case 'CATEGORY': return id(row.categoryId);
      case 'BRAND': return id(row.brandId);
      case 'SUB_BRAND': return id(row.subBrandId);
      case 'ITEM': return id(row.itemId);
      case 'ITEM_CONDITION': return id(row.conditionId);
      case 'STATUS': return text(row.status);
      case 'TRACKING_MODE': return text(row.trackingMode);
      case 'RECEIVED_MONTH': return text(row.receivedMonth, 'No received month');
      default: return 'Unspecified';
    }
  }

  private collectGroupIds(groups: WarehousingStockReportGroup[], parentPath = ''): string[] {
    return groups.flatMap((group, index) => {
      const path = this.groupId(group, parentPath ? `${parentPath}.${index}` : index.toString());
      return [path, ...this.collectGroupIds(group.children || [], path)];
    });
  }

  private groupId(group: WarehousingStockReportGroup, path: string): string {
    return `${path}.${group.dimension}.${group.key}`;
  }

  private searchFilters(): WarehousingSearchFilters {
    return { ...this.searchForm.getRawValue(), metrics: this.metricSelection };
  }

  private itemOptionLabel(item: WarehousingItem): string {
    return [item.itemName, item.itemNumber || '-', item.skuNumber || '-', itemSizeLabel(item)].filter(Boolean).join(' | ');
  }

  private formatNumber(value?: number): string {
    return value === null || value === undefined ? '-' : new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value);
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }
}
